import React from "react";
import { motion } from "framer-motion";
import { Mail, MessageCircle, Download } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { translations } from "../../lib/i18n";
import { TEST_IDS } from "../../constants/testIds";
import { API_BASE } from "../../lib/api";

const Contact = ({ settings = {} }) => {
  const { lang } = useLang();
  const t = translations[lang].contact;

  const email = settings.email || "robertpd13@hotmail.com";
  const phone = settings.phone || "+56 9 54081865";
  const waNumber = phone.replace(/[^\d]/g, "");
  const cvUrl = settings.cv_url
    ? settings.cv_url.startsWith("/api")
      ? `${API_BASE.replace(/\/api$/, "")}${settings.cv_url}`
      : settings.cv_url
    : "";

  return (
    <section
      id="contact"
      data-testid={TEST_IDS.contact.root}
      className="rp-section bg-[color:var(--rp-bg)]"
    >
      <div className="rp-container">
        <div className="grid grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="col-span-12 lg:col-span-7">
            <div className="rp-eyebrow mb-4">11 · {t.eyebrow}</div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rp-heading font-display font-extrabold text-white leading-[1]"
              style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}
            >
              {t.title}
            </motion.h2>
            <p className="mt-8 rp-lede">{t.body}</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="col-span-12 lg:col-span-5 lg:pl-8 lg:border-l lg:border-white/5"
          >
            <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--rp-accent)] mb-6">
              Robert Prieto Deuma
            </div>

            <div className="space-y-6">
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-2">
                  Email
                </div>
                <a
                  href={`mailto:${email}`}
                  className="font-display text-white text-xl md:text-2xl hover:text-[color:var(--rp-accent)] transition-colors break-all"
                >
                  {email}
                </a>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-2">
                  {lang === "es" ? "Teléfono / WhatsApp" : "Phone / WhatsApp"}
                </div>
                <a
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  className="font-display text-white text-xl md:text-2xl hover:text-[color:var(--rp-accent)] transition-colors"
                >
                  {phone}
                </a>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                data-testid={TEST_IDS.contact.email}
                href={`mailto:${email}`}
                className="rp-btn rp-btn-primary"
              >
                <Mail size={14} /> {t.sendEmail}
              </a>
              <a
                data-testid={TEST_IDS.contact.whatsapp}
                href={`https://wa.me/${waNumber}`}
                target="_blank"
                rel="noreferrer"
                className="rp-btn rp-btn-outline"
              >
                <MessageCircle size={14} /> {t.whatsapp}
              </a>
              {cvUrl && (
                <a
                  data-testid={TEST_IDS.contact.cv}
                  href={cvUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rp-btn rp-btn-outline"
                >
                  <Download size={14} /> {t.downloadCv}
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
